<template>
  <section class="override-view" data-testid="tuition-calculator-override">
    <div v-if="formattedAmount" class="override-view__card" data-testid="override-card">
      <div class="override-view__summary">
        <div class="override-view__copy">
          <p class="override-view__label" data-testid="override-heading" tabindex="-1">
            {{ overrideTitle }}
          </p>
          <a
            v-if="connectLink"
            :href="connectLink"
            :target="connectTarget"
            :rel="connectRel"
            class="btn btn-secondary rounded-pill override-view__connect"
            data-testid="override-connect"
            @click="handleConnectClick"
          >
            Connect with a coach
          </a>
        </div>

        <p class="override-view__amount" data-testid="override-amount">
          {{ formattedAmount }}<span aria-hidden="true">*</span>
        </p>
      </div>

      <hr class="override-view__divider" />

      <p class="override-view__learn">
        Learn more about
        <a
          :href="props.program.detailPage ?? undefined"
          class="rds-link"
          data-testid="override-program-link"
          target="_blank"
          rel="noopener noreferrer"
          @click="handleProgramLink"
        >
          {{ props.program.title ?? "this program" }}
        </a>
      </p>

      <div
        v-if="disclaimer"
        class="override-view__disclaimer"
        data-testid="override-disclaimer"
        v-html="disclaimer"
      ></div>
    </div>

    <Alert v-else :show="true" variant="warning" data-testid="override-unavailable">
      Cost information for this program is currently unavailable.
    </Alert>

    <button
      type="button"
      class="btn btn-link tuition-start-over fw-bold fs-large"
      data-testid="override-start-over"
      @click="emit('start-over')"
    >
      <FontAwesomeIcon icon="fa-solid fa-rotate-right" aria-hidden="true" />
      <span>Start over</span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useRoute } from "#imports";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Alert from "~/components/AlertMessage.vue";
import type { DegreeSeekingProgram, WhatItCosts } from "~/server/resolvers/resolvers-types";
import { formatCurrency } from "~/src/services/TuitionCalculatorFormService";
import { buildTrackedCalendlyUrl } from "~/utils/calendlyTracking";

const props = defineProps<{
  pageData: Partial<WhatItCosts>;
  program: DegreeSeekingProgram;
}>();

const emit = defineEmits<{
  (event: "program-link", text: string): void;
  (event: "start-over"): void;
}>();

const CONNECT_WITH_COACH_URL = "https://calendly.com/asu-online-cost-appointments";

const formattedAmount = computed(() => formatCurrency(props.program.costCollection?.amount));

const overrideTitle = computed(
  () => props.pageData.overrideTitle || "The cost of the program per credit hour is:"
);

const firstConnectCard = computed(() => props.pageData.connectCard?.[0]);
const route = useRoute();
const connectLink = computed(() => getTrackedConnectUrl());
const getTrackedConnectUrl = () =>
  buildTrackedCalendlyUrl(
    CONNECT_WITH_COACH_URL,
    route.path,
    typeof document !== "undefined" ? document.cookie : ""
  );
const connectTarget = computed(() => firstConnectCard.value?.link?.target || undefined);
const connectRel = computed(() =>
  connectTarget.value === "_blank" || connectTarget.value === "blank"
    ? "noopener noreferrer"
    : undefined
);
const disclaimer = computed(
  () =>
    props.pageData.overrideDescription ||
    props.pageData.tcDisclaimer ||
    props.pageData.tcFootnote ||
    '<p>*An additional mandatory Student Initiated Fee and Advanced Technology Fee applies each semester and varies based on how many credit hours you take. <a class="non-rds-link" href="https://tuition.asu.edu/cost/tuition-fees" target="_blank">View ASU\'s tuition and fees</a>.</p>'
);

const handleProgramLink = () => {
  emit("program-link", props.program.title ?? "");
};

const handleConnectClick = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLAnchorElement | null;

  if (target) {
    target.href = getTrackedConnectUrl();
  }
};
</script>

<style scoped lang="scss">
.tuition-start-over {
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: fit-content;
  padding: 0;
  color: var(--rds-primary, #8c1d40);
  text-decoration: none;
}

.tuition-start-over span {
  text-decoration: underline;
}

.tuition-start-over:hover,
.tuition-start-over:focus {
  color: var(--rds-primary, #8c1d40);
}

.override-view {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 32px;
}

.override-view__card {
  width: 100%;
  border: 1px solid var(--rds-border-color, #d8d8d8);
  background: #fff;
  padding: 3rem 3.5rem;
}

.override-view__summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2rem;
  align-items: center;
}

.override-view__copy {
  min-width: 0;
}

.override-view__label {
  color: var(--rds-dark-3, #191919);
  font-weight: 700;
  font-size: 2rem;
  line-height: 1.25;
  margin-bottom: 1.5rem;
  max-width: 25rem;
}

.override-view__amount {
  color: var(--rds-success, #446d12);
  font-weight: 700;
  font-size: 5rem;
  margin-bottom: 0;
  line-height: 1;
  white-space: nowrap;
}

.override-view__connect {
  color: var(--rds-dark-3, #191919);
  font-weight: 700;
  padding: 0.5rem 1.5rem;
}

.override-view__divider {
  border: 0;
  border-top: 1px solid var(--rds-border-color, #d8d8d8);
  margin: 2.5rem 0 2rem;
  opacity: 1;
}

.override-view__learn,
.override-view__disclaimer {
  color: var(--rds-dark-2, #484848);
  font-size: 1.125rem;
  line-height: 1.5;
}

.override-view__learn {
  color: var(--rds-dark-3, #191919);
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.override-view__disclaimer {
  :deep(p) {
    margin-bottom: 0;
  }
}

.override-view__start-over {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  color: var(--rds-primary, #8c1d40);
  font-weight: 700;
  text-decoration: underline;
}

.override-view__start-over:hover,
.override-view__start-over:focus {
  color: var(--rds-primary, #8c1d40);
}

@media (max-width: 991.98px) {
  .override-view {
    gap: 32px;
  }

  .override-view__card {
    padding: 2rem 2rem 2.25rem;
    text-align: center;
  }

  .override-view__summary {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
  }

  .override-view__copy {
    display: contents;
  }

  .override-view__label {
    order: 1;
    align-self: center;
    max-width: 22rem;
    font-size: 1.75rem;
    line-height: 1.2;
    margin-bottom: 0;
  }

  .override-view__amount {
    order: 2;
    font-size: 4.75rem;
  }

  .override-view__connect {
    order: 3;
    align-self: center;
    padding: 0.5rem 1.25rem;
  }

  .override-view__divider {
    margin: 2.25rem 0 1.75rem;
  }

  .override-view__learn,
  .override-view__disclaimer {
    text-align: left;
    font-size: 1rem;
  }

  .override-view__start-over {
    margin-top: 0.75rem;
  }
}
</style>
